/********************************/
/*     LAYER MANAGER CLASS      */
/********************************/

/**
 * Manages all rendering layers and coordinates their updates
 */
export class LayerManager {
  constructor() {
    this.layers = new Map();
    this.layerOrder = [];
  }

  /**
   * Add a layer to the manager
   * @param {string} name - Unique identifier for the layer
   * @param {BaseLayer} layer - Layer instance
   * @param {number} order - Rendering order (lower numbers render first)
   */
  addLayer(name, layer, order = 0) {
    this.layers.set(name, {
      instance: layer,
      order: order
    });

    // Update layer order array
    this._updateLayerOrder();
  }

  /**
   * Get a layer by name
   * @param {string} name - Layer identifier
   * @returns {BaseLayer|undefined}
   */
  getLayer(name) {
    const layer = this.layers.get(name);
    return layer ? layer.instance : undefined;
  }

  /**
   * Initialize all layers
   * @param {p5.Font} font - The font to use for text rendering
   */
  init(font) {
    for (const [name, layerData] of this.layers) {
      layerData.instance.init(font);
    }
  }

  /**
   * Update all layers with current state
   * @param {Object} state - Animation state {loopPercent, rotationX, rotationY}
   */
  updateAll(state) {
    for (const name of this.layerOrder) {
      const layerData = this.layers.get(name);
      layerData.instance.updateState(state);
    }
  }

  /**
   * Render all layers (in order)
   */
  renderAll() {
    for (const name of this.layerOrder) {
      const layerData = this.layers.get(name);
      layerData.instance.render();
    }
  }

  /**
   * Render a specific layer by name
   * @param {string} name - Layer identifier
   */
  renderLayer(name) {
    const layerData = this.layers.get(name);
    if (layerData) {
      layerData.instance.render();
    }
  }

  /**
   * Get a layer's buffer by name
   * @param {string} name - Layer identifier
   * @returns {Framebuffer|Graphics|undefined}
   */
  getBuffer(name) {
    const layerData = this.layers.get(name);
    return layerData ? layerData.instance.getBuffer() : undefined;
  }

  /**
   * Remove a layer by name
   * @param {string} name - Layer identifier
   */
  removeLayer(name) {
    this.layers.delete(name);
    this._updateLayerOrder();
  }

  /**
   * Update internal layer order array based on order values
   * @private
   */
  _updateLayerOrder() {
    this.layerOrder = Array.from(this.layers.entries())
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([name]) => name);
  }

  /**
   * Get all layer names in rendering order
   * @returns {string[]}
   */
  getLayerNames() {
    return [...this.layerOrder];
  }

  /**
   * Resize all layers
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resizeAll(width, height) {
    for (const [name, layerData] of this.layers) {
      layerData.instance.resize(width, height);
    }
  }
}
